import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import PropTypes from 'prop-types';
// import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import * as AppPropTypes from '../../../lib/PropTypes';

// import { useContest } from '../../../contexts/ContestContext';
import { useThemeClassName } from '../../../contexts/ThemeContext';
import StyledButton from '../../buttons/StyledButton';
import EmailForm from '../../forms/Email';
import DrawCard from '../../items/DrawCard';
import PageTitle from '../../partials/PageTitle';
import PrizeCard from '../../partials/PrizeCard';
import Prizes from '../../partials/Prizes';
import SportParticipationsStats from '../../partials/SportParticipationsStats';

import styles from '../../../../styles/pages/account-nhl.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    loading: PropTypes.bool.isRequired,
    reload: PropTypes.func.isRequired,
};

function AccountNHL({ page, loading, reload }) {
    const themeClassName = useThemeClassName(styles);
    // console.log('page', page);
    // const {
    //     outcome: { hasLeaderboard = false },
    // } = useContest();
    const intl = useIntl();
    const user = useUser();

    const { stats, draws, points } = user || {};
    const route = useUrlGenerator();

    const {
        prizes = null,
        title = null,
        withEmailForm = false,
        withoutEmptyDraws = true,
    } = page || {};

    const { prizes: totalPrizesCount = 0 } = stats || {}; // pointsToday

    const instantPrizes = (prizes || []).filter((prize) => prize.handle === 'end');
    const drawPrizes = (prizes || []).filter((prize) => prize.handle !== 'end');
    const { length: prizesCount } = prizes || [];

    const prizesPositionRef = useRef(null);
    const onPrizeClaim = useCallback(() => {
        const { offsetTop = null } = prizesPositionRef?.current || {};
        setTimeout(() => {
            if (offsetTop !== null && typeof window !== 'undefined') {
                window.scroll({
                    top: offsetTop - 20,
                    behavior: 'smooth',
                });
            }
        }, 300);
        if (reload !== null) {
            reload();
        }
    }, [user, reload, loading]);

    const finalDraws = useMemo(() => {
        if (withoutEmptyDraws) {
            return (draws || []).filter((draw) => {
                const { totalPoints = 0 } = draw || {};
                return totalPoints > 0;
            });
        }
        return draws || [];
    }, [draws, withoutEmptyDraws]);

    return (
        <div className={classNames([styles.container, themeClassName])}>
            <PageTitle className={styles.pageTitle} title={title} />

            {instantPrizes.length > 0 ? (
                <div ref={prizesPositionRef} className={styles.section}>
                    <Prizes
                        prizes={instantPrizes}
                        prizeTitle={
                            <FormattedMessage
                                defaultMessage="Instant prize"
                                description="Button label"
                            />
                        }
                        className={styles.prizes}
                        onPrizeClaim={onPrizeClaim}
                    />
                </div>
            ) : null}

            <div className={styles.section}>
                {(finalDraws || []).map((draw, drawIndex) => (
                    <div key={`draw-${drawIndex + 1}`} className={styles.draw}>
                        <DrawCard draw={draw} className={styles.drawCard} />
                    </div>
                ))}
            </div>

            {drawPrizes !== null && drawPrizes.length > 0 ? (
                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>
                        <FormattedMessage
                            defaultMessage="Historic"
                            description="Account section title"
                        />
                    </h2>
                    <div className={styles.sectionSubtitle}>
                        <FormattedMessage
                            defaultMessage="Prizes won"
                            description="Account section subtitle"
                        />
                    </div>
                    <div className={styles.prizes}>
                        {(drawPrizes || []).map((prize, prizeIndex) => {
                            const {
                                prize: prizeObject,
                                uuid: prizeUuid,
                                claimed: prizeClaimed,
                            } = prize || {};
                            const { image: prizeImage, name: prizeName } = prizeObject || {};
                            return (
                                <PrizeCard
                                    className={styles.prize}
                                    key={`prize-${prizeIndex + 1}`}
                                    href={route('prize', { participant_prize: prizeUuid })}
                                    name={prizeName}
                                    image={prizeImage}
                                    status={
                                        prizeClaimed ? (
                                            <FormattedMessage
                                                defaultMessage="Claimed"
                                                description="Prize status label"
                                            />
                                        ) : null
                                    }
                                />
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {totalPrizesCount > prizesCount && prizesCount > 0 ? (
                <div className={styles.allPrizesLinkContainer}>
                    <StyledButton className={styles.allPrizesLink} href={route('prizes')}>
                        <FormattedMessage
                            defaultMessage="All your prizes"
                            description="Button label"
                        />
                    </StyledButton>
                </div>
            ) : null}

            <SportParticipationsStats
                className={styles.section}
                titleClassName={styles.sectionTitle}
                stats={stats}
                points={points}
            />

            {withEmailForm ? (
                <div className={styles.emailFormContainer}>
                    <div className={styles.emailFormInner}>
                        <h2 className={styles.emailTitle}>
                            <FormattedMessage
                                defaultMessage="Want better odds of winning?"
                                description="NHL Email form title"
                            />
                        </h2>
                        <div className={styles.emailDescription}>
                            <FormattedMessage
                                defaultMessage="Enter your email address for an additional chance to win!"
                                description="NHL Email form description"
                            />
                        </div>
                        <EmailForm
                            className={styles.emailForm}
                            placeholder={intl.formatMessage({
                                defaultMessage: 'email@johndoe.com',
                                description: 'NHL Email form placeholder',
                            })}
                            inputClassName={styles.emailInput}
                            submitClassName={styles.emailSubmit}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
}

AccountNHL.propTypes = propTypes;

export default AccountNHL;
